import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_API_URL;


const Homepage = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/categories`),
        axios.get(`${BACKEND_URL}/api/products`)
      ]);
      setCategories(categoriesRes.data);
      setFeaturedProducts(productsRes.data.slice(0, 8));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="hero-section relative flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1765118106411-dccdfb21f77d)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="hero-overlay"></div>
        <div className="relative z-10 text-center max-w-4xl px-4">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="hero-title">
            Premium Lighting Solutions<br />for Modern Spaces
          </h1>
          <p className="text-lg sm:text-xl mb-8 text-white/90">
            Elevate your environment with our curated collection of luxury lighting
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="bg-[#BFA15F] text-white hover:bg-[#A68B4F] rounded-none px-8 py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300 inline-flex items-center justify-center gap-2"
              data-testid="shop-lighting-btn"
            >
              Shop Lighting <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`https://wa.me/9779816315999?text=${encodeURIComponent('Hello Avex Enterprises, I would like to inquire about your lighting products.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] rounded-none px-8 py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300"
              data-testid="whatsapp-btn"
            >
              Order on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" data-testid="categories-heading">Explore Our Collections</h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              From elegant chandeliers to modern track lighting, discover the perfect illumination for every space
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.slug}`}
                className="category-card group relative overflow-hidden bg-white border border-[#E5E5E5] hover:border-[#BFA15F] transition-all duration-300 min-h-[300px]"
                data-testid={`category-${category.slug}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={category.image_url}
                    alt={category.name}
                    className="category-image w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-[#BFA15F] transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-sm text-[#4A4A4A]">{category.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" data-testid="featured-heading">Featured Products</h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              Handpicked selections from our premium collection
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="product-card-hover group bg-white border border-transparent hover:border-[#E5E5E5] transition-all duration-300 p-4"
                data-testid={`featured-product-${product.id}`}
              >
                <div className="relative h-64 overflow-hidden mb-4 bg-[#F0F0ED]">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="product-image-zoom w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-[#BFA15F] transition-colors">
                  {product.name}
                </h3>
                <p className="text-[#4A4A4A] text-sm mb-3 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#BFA15F]">NPR {product.price.toLocaleString()}</span>
                  <span className="text-xs text-[#888888] uppercase tracking-wider">View Details</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-3 uppercase tracking-widest text-sm transition-all duration-300 inline-flex items-center gap-2"
              data-testid="view-all-products-btn"
            >
              View All Products <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Why Choose Avex Enterprises</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Premium Quality</h3>
              <p className="text-[#4A4A4A]">
                Curated selection of high-quality lighting fixtures from trusted manufacturers
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Expert Guidance</h3>
              <p className="text-[#4A4A4A]">
                Professional consultation to help you find the perfect lighting for your space
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#BFA15F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Nepal-Wide Service</h3>
              <p className="text-[#4A4A4A]">
                Serving customers across Nepal with reliable delivery and support
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;